import { useEffect } from "react";
import { matchPath, NavLink, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import type { MeResponse } from "@telecurb/shared";
import { useMe } from "@/auth/queries";
import { useSignOut } from "@/auth/mutations";
import { getMyProfile } from "@/lib/profile";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { IdleTimeout } from "@/components/IdleTimeout";
import { Lockup } from "@/components/brand/lockup";
import { UserMenu } from "@/components/telecurb/user-menu";
import { useSidebarCollapsed } from "@/hooks/use-sidebar-collapsed";
import { summarizeSpecialties } from "@/lib/specialty-display";
import {
  LayoutDashboard,
  FileText,
  Building2,
  Users,
  ClipboardList,
  Settings,
  HelpCircle,
  Archive,
  History,
  Inbox,
  CircleCheckBig,
  MessageSquareReply,
  Stethoscope,
  BookOpen,
} from "lucide-react";

export type Me = MeResponse;

export interface ShellContext {
  me: Me;
}

type NavItem = {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  // Exact-match the active state (like /dashboard) instead of prefix-match. Used
  // by the org-root "Inbox" item so it doesn't stay highlighted on child routes.
  end?: boolean;
};

type NavSection = {
  title?: string;
  items: NavItem[];
};

const PLATFORM_ADMIN_NAV: NavSection[] = [
  {
    items: [
      { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
      { label: "Consults", to: "/consults", icon: FileText },
      { label: "Organization Management", to: "/organizations", icon: Building2 },
      { label: "User Management", to: "/users", icon: Users },
      { label: "Specialties", to: "/specialties", icon: Stethoscope },
    ],
  },
  {
    title: "Operations",
    items: [
      { label: "Audit Logs", to: "/audit-logs", icon: ClipboardList },
      { label: "Platform Settings", to: "/platform-settings", icon: Settings },
    ],
  },
  {
    title: "Other",
    items: [
      { label: "Specialist Handbook", to: "/handbook", icon: BookOpen },
      { label: "Settings", to: "/account", icon: Settings },
      { label: "Support", to: "/support", icon: HelpCircle },
    ],
  },
];

const PRACTICE_ADMIN_NAV: NavSection[] = [
  {
    items: [
      { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
      { label: "Consults", to: "/consults", icon: FileText },
      { label: "User Management", to: "/users", icon: Users },
      { label: "Organization Settings", to: "/org-settings", icon: Settings },
    ],
  },
  {
    title: "Other",
    items: [
      { label: "Settings", to: "/account", icon: Settings },
      { label: "Support", to: "/support", icon: HelpCircle },
    ],
  },
];

// Shown only transiently while a submitter with no resolvable org is being
// redirected (edge case — pcp/css normally have me.organizationId).
const MINIMAL_NAV: NavSection[] = [
  {
    title: "Other",
    items: [
      { label: "Settings", to: "/account", icon: Settings },
      { label: "Support", to: "/support", icon: HelpCircle },
    ],
  },
];

const SPECIALIST_NAV: NavSection[] = [
  {
    items: [
      { label: "Inbox", to: "/inbox", icon: Inbox },
      { label: "Respond", to: "/respond", icon: MessageSquareReply },
      { label: "Completed", to: "/completed", icon: CircleCheckBig },
    ],
  },
  {
    title: "Other",
    items: [
      { label: "Specialist Handbook", to: "/handbook", icon: BookOpen },
      { label: "Settings", to: "/account", icon: Settings },
      { label: "Support", to: "/support", icon: HelpCircle },
    ],
  },
];

const CURATOR_ROOT_NAV: NavSection[] = [
  {
    items: [{ label: "Organizations", to: "/orgs", icon: Building2 }],
  },
  {
    title: "Other",
    items: [
      { label: "Settings", to: "/account", icon: Settings },
      { label: "Support", to: "/support", icon: HelpCircle },
    ],
  },
];

// Org-scoped submitter nav, shared by pcp/css and curators (all now run under
// /orgs/:orgId). `showAllOrgs` adds the org switcher — only multi-org users
// (curators) get it; single-org pcp/css don't.
function orgNav(orgId: string, showAllOrgs: boolean): NavSection[] {
  return [
    {
      items: [
        // end: exact-match so the org root only highlights on the inbox itself,
        // not on /drafts, /submitted, /history (all prefixed by /orgs/:orgId).
        { label: "Inbox", to: `/orgs/${orgId}`, icon: Inbox, end: true },
        { label: "Drafts", to: `/orgs/${orgId}/drafts`, icon: FileText },
        { label: "Pending", to: `/orgs/${orgId}/submitted`, icon: ClipboardList },
        { label: "Completed", to: `/orgs/${orgId}/history`, icon: Archive },
        // TEL-116: org-wide browse — every provider's non-draft consults.
        { label: "Organization History", to: `/orgs/${orgId}/org-history`, icon: History },
      ],
    },
    {
      title: "Other",
      items: [
        ...(showAllOrgs
          ? [{ label: "All Organizations", to: "/orgs", icon: Building2, end: true }]
          : []),
        { label: "Settings", to: "/account", icon: Settings },
        { label: "Support", to: "/support", icon: HelpCircle },
      ],
    },
  ];
}

const ROLE_LABEL: Record<string, string> = {
  platform_admin: "Platform Administrator",
  practice_admin: "Practice Administrator",
  pcp: "Primary Care",
  clinical_support_staff: "Clinical Support Staff",
  consult_curator: "Curator",
  specialist: "Specialist",
};

function roleLabel(role: string | null): string {
  return ROLE_LABEL[role ?? ""] ?? "";
}

function navConfig(
  role: string | null,
  activeOrgId?: string | null,
): { sections: NavSection[]; roleName: string } {
  if (role === "platform_admin") return { sections: PLATFORM_ADMIN_NAV, roleName: roleLabel(role) };
  if (role === "practice_admin") return { sections: PRACTICE_ADMIN_NAV, roleName: roleLabel(role) };
  // All submitters run under /orgs/:orgId and share the org nav, keyed on the
  // active org (route param for curators, own org for pcp/css). Curators get the
  // "All orgs" switcher; pcp/css don't. Without an active org, a curator picks
  // one (CURATOR_ROOT_NAV); a pcp/css is mid-redirect → minimal nav.
  if (role === "pcp" || role === "clinical_support_staff" || role === "consult_curator") {
    if (activeOrgId)
      return {
        sections: orgNav(activeOrgId, role === "consult_curator"),
        roleName: roleLabel(role),
      };
    if (role === "consult_curator")
      return { sections: CURATOR_ROOT_NAV, roleName: roleLabel(role) };
    return { sections: MINIMAL_NAV, roleName: roleLabel(role) };
  }
  if (role === "specialist") return { sections: SPECIALIST_NAV, roleName: roleLabel(role) };
  return { sections: [], roleName: roleLabel(role) };
}

// The mobile sidebar is an off-canvas sheet; React Router navigation doesn't
// dismiss it, so it stayed open over the freshly-navigated page (TEL-134).
// Close it on every pathname change. No-op on desktop — setOpenMobile only
// drives the mobile sheet. Must be a child of SidebarProvider to read context.
function CloseMobileNavOnNavigate() {
  const { setOpenMobile } = useSidebar();
  const { pathname } = useLocation();
  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);
  return null;
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const { data: me, isPending } = useMe();
  const { data: profile } = useQuery({
    queryKey: ["me", "profile"],
    queryFn: getMyProfile,
    enabled: me?.role === "specialist",
  });
  const signOutMutation = useSignOut();

  const orgMatch = useMatch("/orgs/:orgId/*");
  const routeOrgId = orgMatch?.params.orgId ?? null;
  // The active org for the shell: the /orgs/:orgId route param for curators, or
  // the user's own org for single-org pcp/css. This is the ONE place
  // me.organizationId is read for routing — everything downstream uses the URL.
  const activeOrgId =
    routeOrgId ??
    (me?.role === "pcp" || me?.role === "clinical_support_staff"
      ? (me?.organizationId ?? null)
      : null);
  const { sections, roleName } = navConfig(me?.role ?? null, activeOrgId);
  const specialistSpecialties = me?.role === "specialist" ? profile?.specialties : undefined;
  const specialistRoleName = specialistSpecialties
    ? summarizeSpecialties(specialistSpecialties)
    : null;
  const specialistRoleTitle = specialistSpecialties?.length
    ? specialistSpecialties.join(", ")
    : undefined;
  const displayRoleName = specialistRoleName ?? roleName;

  async function handleSignOut() {
    try {
      await signOutMutation.mutateAsync();
      navigate("/sign-in", { replace: true });
    } catch {
      toast.error("Sign out failed. Please try again.");
    }
  }

  // RequireAuth already gated on a non-null `me`, but React Query exposes
  // `data` as `T | undefined` during initial fetch — narrow it here.
  if (isPending || !me) return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;

  // Only show the New consult CTA when there's an active org — a no-org pcp/css
  // renders the not-scoped dashboard (activeOrgId null), and the bare wizard
  // route no longer exists, so there's nowhere for the CTA to point otherwise.
  const isSubmitter =
    activeOrgId !== null &&
    (me.role === "pcp" || me.role === "clinical_support_staff" || me.role === "consult_curator");
  const newConsultPath = activeOrgId ? `/orgs/${activeOrgId}/consults/new` : "/dashboard";
  const homePath = me.role === "specialist" ? "/inbox" : "/dashboard";

  return (
    <SidebarProvider
      open={!collapsed}
      onOpenChange={(o) => setCollapsed(!o)}
      style={{ "--header-height": "3.5rem" } as React.CSSProperties}
      className="h-svh min-h-svh flex-col"
    >
      <IdleTimeout />
      <CloseMobileNavOnNavigate />
      {/* Full-width top nav. The stock SidebarTrigger controls both the mobile
          sheet and desktop icon collapse. */}
      <header className="h-14 border-b bg-background sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-background/85">
        <div className="flex h-full items-center">
          <div className="flex w-(--sidebar-width-icon) shrink-0 items-center justify-center">
            <SidebarTrigger />
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-3 pr-4 md:pl-8 md:pr-6">
            <NavLink
              to={homePath}
              className="flex shrink-0 items-center"
              aria-label="TeleCurbMD — home"
            >
              <Lockup className="h-5 text-brand-navy dark:text-foreground" />
            </NavLink>
            <div className="ml-auto flex min-w-0 items-center gap-2 md:gap-4">
              {isSubmitter && (
                <Button asChild size="sm" className="px-3">
                  <NavLink to={`${newConsultPath}/patient`} aria-label="New Consult">
                    New Consult
                  </NavLink>
                </Button>
              )}
              {/* Account menu. Sign out lives in here rather than as a standalone
                icon button so it isn't a tap target flush against the New
                consult CTA on mobile, where missing it by a few pixels
                logged the user out (TEL-133). */}
              <UserMenu
                displayName={me.displayName}
                tagline={displayRoleName}
                title={specialistRoleTitle}
                onSignOut={handleSignOut}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar + content row. The patched sidebar.tsx offsets the fixed
          panel by --header-height so it sits flush below the header. */}
      <div className="flex flex-1 min-h-0 w-full">
        <Sidebar collapsible="icon">
          <SidebarContent>
            {sections.map((section, i) => (
              <SidebarGroup key={section.title ?? section.items[0]?.to ?? String(i)}>
                {section.title && <SidebarGroupLabel>{section.title}</SidebarGroupLabel>}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {section.items.map((item) => {
                      const end = item.end ?? item.to === "/dashboard";
                      const isActive = Boolean(
                        matchPath({ path: item.to, end }, location.pathname),
                      );
                      return (
                        <SidebarMenuItem key={item.to}>
                          <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                            <NavLink to={item.to} end={end}>
                              <item.icon />
                              <span>{item.label}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
          <SidebarFooter />
          <SidebarRail />
        </Sidebar>

        <SidebarInset>
          <Outlet context={{ me } satisfies ShellContext} />
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
